'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Language } from '@/lib/api';
import FlagIcon, { useFlagVisible } from './FlagIcon';

/**
 * The "all languages" panel behind the ▾ on each half of the language bar.
 *
 * With ~100 languages a plain dropdown list is unusable — it would be a
 * thousand-pixel scroll with no way in. So: a search box that filters as you
 * type, and a multi-column grid so the whole set is scannable in one or two
 * screens.
 *
 * Search matches the English name AND the endonym, so someone can type either
 * "Korean" or "조선" and find it. The endonym is not *displayed* (the tabs show
 * English names only, by request) — it is only there to be found by.
 */
export default function LanguagePicker({
  languages,
  selected,
  onPick,
  onClose,
  align = 'left',
  includeAuto = false,
  onPickAuto,
  detectedName,
}: {
  languages: Language[];
  selected: string;
  onPick: (code: string) => void;
  onClose: () => void;
  align?: 'left' | 'right';
  /** Source side only: offer "Detect language" as the first option. */
  includeAuto?: boolean;
  onPickAuto?: () => void;
  detectedName?: string | null;
}) {
  const [query, setQuery] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  /*
   * As tall as the window has room for under the bar, up to 19rem — measured,
   * because where the panel starts depends on the layout (one bar or two
   * stacked halves) and on how far the page is scrolled. In a short window the
   * list scrolls inside the panel instead of running off the bottom of the
   * screen, where its last rows could only be reached by scrolling the page
   * out from under the search box. Never below 9rem: three rows is the least
   * that still reads as a list.
   */
  const [room, setRoom] = useState<number | null>(null);
  useEffect(() => {
    const measure = () => {
      const top = list.current?.getBoundingClientRect().top;
      if (top != null) setRoom(Math.max(144, window.innerHeight - top - 12));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    input.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return languages;
    return languages.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.nativeName.toLowerCase().includes(q) ||
        l.code.toLowerCase() === q,
    );
  }, [languages, query]);

  return (
    <div
      ref={box}
      role="dialog"
      aria-label="Choose a language"
      // `top-full` is load-bearing: an absolutely positioned child of a FLEX
      // row with no `top` falls back to its static position, which put the
      // panel at the flex line's origin — i.e. drawn upward, with the search box
      // clipped off the top of the window. Anchor it below the bar explicitly.
      //
      // z-50 over the panes; the parent bar carries z-20 so this is never
      // painted under the input card.
      className={
        // Widened from 34rem when the list grew to the full Google Translate
        // set. That set is full of names whose meaning lives in a parenthetical
        // — Crimean Tatar (Latin) vs (Cyrillic), Inuktut (Latin) vs
        // (Syllabics), Santali (Latin) vs (Ol Chiki) — and those pairs share a
        // flag, so a column too narrow to show the bracket makes them literally
        // indistinguishable. 44rem is what fits the longest of them in three
        // columns; `min` with the viewport keeps it honest on small screens.
        'absolute top-full z-50 mt-1 w-[min(94vw,44rem)] overflow-hidden rounded-xl border ' +
        'border-ink-200 bg-white shadow-2xl dark:border-ink-700 dark:bg-ink-800 ' +
        (align === 'right' ? 'right-0' : 'left-0')
      }
    >
      <div className="border-b border-ink-100 p-2 dark:border-ink-700">
        <input
          ref={input}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${languages.length} languages…`}
          className="w-full rounded-lg bg-ink-100 px-3 py-2 text-sm text-ink-800 outline-none
                     placeholder:text-ink-400 focus:ring-1 focus:ring-accent-500
                     dark:bg-ink-900 dark:text-ink-dim"
        />
      </div>

      <div ref={list} className="max-h-[19rem] overflow-y-auto p-1.5"
           style={room != null ? { maxHeight: `min(19rem, ${room}px)` } : undefined}>
        {includeAuto && !query && (
          <button
            type="button"
            onClick={() => { onPickAuto?.(); onClose(); }}
            className={
              'mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition ' +
              (selected === 'auto'
                ? 'bg-accent-200 font-medium text-accent-700 dark:bg-accent-600/25 dark:text-ink-dim'
                : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-700')
            }
          >
            <span>{detectedName ? `${detectedName} - Detected` : 'Detect language'}</span>
            {selected === 'auto' && <Tick />}
          </button>
        )}

        {filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-ink-400">
            No language matches “{query}”.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-0.5 sm:grid-cols-3">
            {filtered.map((l) => {
              const on = l.code === selected;
              return (
                <button
                  key={l.code}
                  type="button"
                  // Two columns on a phone will always clip the longest names,
                  // whatever the panel is doing on a desktop.
                  title={l.name}
                  onClick={() => { onPick(l.code); onClose(); }}
                  className={
                    'flex items-center justify-between gap-1 rounded-lg px-3 py-1.5 text-left text-sm transition ' +
                    (on
                      ? 'bg-accent-200 font-medium text-accent-700 dark:bg-accent-600/25 dark:text-ink-dim'
                      : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-700')
                  }
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Flags codes={l.countries ?? []} />
                    <span className="truncate">{l.name}</span>
                  </span>
                  {on && <Tick />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** How far the middle flag rides above the others. */
const STAGGER_PX = 3;

/**
 * Up to three country flags identifying a language.
 *
 * Overlapped rather than laid out in a row: three flags side by side would push
 * the names out of alignment across the grid columns, and the slot has to be a
 * fixed width for the list to stay scannable. The ring gives each flag an edge
 * so an overlap still reads as separate flags.
 *
 * They are also staggered: the second one sits a few pixels proud of the rest,
 * so three flags read as an arch and two as a step rather than as one flat bar
 * of colour. Overlapping alone left the group looking like a single smeared
 * rectangle at this size — the break in the top edge is what makes the count
 * legible at a glance.
 *
 * One rule covers both cases the slot ever shows. With three, lifting the middle
 * leaves the outer two level with each other; with two, it lifts the second
 * above the first. With one there is nothing to stagger.
 *
 * All of them are cut out of one sprite — see components/FlagIcon.tsx. This
 * panel is the reason that sprite exists: a hundred languages with three flags
 * each meant opening this dropdown fired ~250 requests, which is what made it
 * slow to appear.
 */
function Flags({ codes }: { codes: string[] }) {
  // Filtered BEFORE slicing, because FlagIcon renders nothing for a code the
  // sprite has no artwork for. Leaving those in the list means the positions are
  // computed for flags that never appear — the first visible flag would inherit
  // a negative margin meant for an overlap with something invisible, and the
  // lift could land on the wrong one, or on none of them.
  //
  // `useFlagVisible` rather than `hasFlag`, so a flag switched off in
  // Settings ▸ Flags drops out of the arch and the remaining one or two close
  // up properly instead of leaving a gap where it used to sit. A language whose
  // every flag is hidden keeps its empty fixed-width slot, which is what holds
  // the three columns of names in line.
  const visible = useFlagVisible();
  const shown = codes.filter(visible).slice(0, 3);
  return (
    <span className="flex w-[34px] shrink-0 items-center" aria-hidden>
      {shown.map((c, i) => (
        <FlagIcon
          key={c}
          code={c}
          className="h-[13px] w-[17px] rounded-[2px] ring-1 ring-black/15 dark:ring-white/20"
          style={{
            marginLeft: i === 0 ? 0 : -8,
            zIndex: shown.length - i,
            // A transform, not a margin: the raised flag must not add height to
            // the row or the list stops being an even grid.
            transform: i === 1 ? `translateY(-${STAGGER_PX}px)` : undefined,
          }}
        />
      ))}
    </span>
  );
}

function Tick() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
